import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronDown, ChevronRight, Mail, CalendarCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { serviceStructure } from '../data/services';

const LOGO_URL = "/dnb_final_header_logo_transparent.png";
const PHONE = "403-615-8770";
const EMAIL = "Radinovicw@shaw.ca";

const navItems = [
  { name: 'Plumbing', path: '/plumbing', hasDropdown: true },
  { name: 'Heating', path: '/heating', hasDropdown: true },
  { name: 'Gas Fitting', path: '/gas-fitting', hasDropdown: true },
  { name: 'Expert Tips', path: '/blog' },
  { name: 'About', path: '/about' },
];

// Top utility bar — emergency message + contact details
export const TopBar = () => {
  return (
    <div className="bg-[#0F172A] text-white text-[12.5px] sm:text-sm">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        {/* Mobile: only emergency. Desktop: emergency + serving line */}
        <div className="flex items-center gap-2 min-w-0">
          <span className="relative flex h-2 w-2 flex-shrink-0">
            <span className="absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75 animate-ping"></span>
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500"></span>
          </span>
          <span className="font-bold tracking-wide text-white whitespace-nowrap">
            24 HR Emergency Service
          </span>
          <span className="hidden md:inline text-slate-400">•</span>
          <span className="hidden md:inline text-slate-300 truncate">
            Serving Calgary &amp; Surrounding Areas Since 2009
          </span>
        </div>

        {/* Right side — email + phone (desktop only) */}
        <div className="hidden sm:flex items-center gap-5 flex-shrink-0">
          <a
            href={`mailto:${EMAIL}`}
            data-testid="topbar-email"
            className="hidden lg:flex items-center gap-1.5 text-slate-300 hover:text-white transition-colors"
          >
            <Mail className="w-3.5 h-3.5" />
            <span>{EMAIL}</span>
          </a>
          <a
            href={`tel:${PHONE}`}
            data-testid="topbar-phone"
            className="flex items-center gap-1.5 font-bold text-white hover:text-[#38BDF8] transition-colors"
          >
            <Phone className="w-3.5 h-3.5" />
            <span>{PHONE}</span>
          </a>
        </div>
      </div>
    </div>
  );
};

// Service Dropdown Component
const ServiceDropdown = ({ service, isOpen, onClose }) => {
  const serviceData = serviceStructure[service];
  if (!serviceData) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.15 }}
          className="absolute top-full left-0 mt-3 w-80 bg-white shadow-2xl rounded-2xl py-3 border border-slate-200 z-50 ring-1 ring-sky-50"
          onMouseLeave={onClose}
        >
          <div className="px-5 pb-3 mb-2 border-b border-slate-100">
            <Link
              to={`/${service}`}
              className="text-[#0F172A] font-bold text-sm hover:text-[#0284C7] transition-colors inline-flex items-center gap-1"
            >
              All {serviceData.name} Services
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {serviceData.subServices.map((sub) => (
              <Link
                key={sub.slug}
                to={`/${service}/${sub.slug}`}
                className="flex items-center gap-2 px-5 py-2.5 text-sm text-slate-600 hover:text-[#0F172A] hover:bg-slate-50/60 transition-colors"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-sky-300"></span>
                {sub.name}
              </Link>
            ))}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [expandedMobileService, setExpandedMobileService] = useState(null);
  const location = useLocation();
  const dropdownTimeoutRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenDropdown(null);
    setExpandedMobileService(null);
  }, [location]);

  const handleMouseEnter = (service) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
    }
    setOpenDropdown(service);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 150);
  };

  const getServiceSlug = (name) => {
    return name.toLowerCase().replace(/ /g, '-');
  };

  return (
    <>
      <TopBar />

      <header
        data-testid="main-header"
        className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md ${
          isScrolled ? 'shadow-md border-b border-slate-200' : 'border-b border-slate-100'
        }`}
      >
        {/* === ROW 1: Logo + Right-side CTA (desktop) / Logo + call + hamburger (mobile) === */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            {/* Logo */}
            <Link to="/" data-testid="logo-link" className="flex items-center group">
              <img
                src={LOGO_URL}
                alt="DNB Plumbing, Heating & Gas Fitting Inc."
                className="h-16 sm:h-20 md:h-24 lg:h-[104px] w-auto object-contain transition-transform group-hover:scale-[1.02]"
              />
            </Link>

            {/* Desktop right side — Phone block (Book Service moved to nav row) */}
            <div className="hidden md:flex items-center gap-4 lg:gap-6">
              <a
                href={`tel:${PHONE}`}
                data-testid="header-phone"
                className="flex items-center gap-3 group"
              >
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-slate-100 group-hover:bg-[#0284C7] text-[#0284C7] group-hover:text-white transition-colors">
                  <Phone className="w-5 h-5" strokeWidth={2.2} />
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">Call Mike Direct</span>
                  <span className="font-heading font-extrabold text-lg text-[#0F172A] group-hover:text-[#0284C7] transition-colors">
                    {PHONE}
                  </span>
                </span>
              </a>
            </div>

            {/* Mobile - call icon + hamburger */}
            <div className="flex items-center gap-1 md:hidden">
              <a
                href={`tel:${PHONE}`}
                data-testid="mobile-call-icon"
                aria-label="Call DNB Plumbing"
                className="p-2.5 text-[#0F172A] hover:text-[#0284C7] transition-colors"
              >
                <Phone className="w-6 h-6" strokeWidth={2.2} />
              </a>
              <button
                data-testid="mobile-menu-btn"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 text-slate-700 hover:text-[#0284C7] transition-colors"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
              </button>
            </div>
          </div>
        </div>

        {/* === ROW 3: Desktop Nav Row (nav items left + Book Service button right) === */}
        <div className="hidden md:block border-t border-slate-200/70 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-4 py-1.5">
              <nav className="flex items-center gap-1">
                {navItems.map((item) => (
                  <div
                    key={item.name}
                    className="relative"
                    onMouseEnter={() => item.hasDropdown && handleMouseEnter(getServiceSlug(item.name))}
                    onMouseLeave={handleMouseLeave}
                  >
                    <Link
                      to={item.path}
                      data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
                      className={`flex items-center gap-1.5 px-4 lg:px-5 py-3.5 font-heading font-semibold text-[15px] tracking-tight transition-all relative ${
                        location.pathname.startsWith(item.path) && item.path !== '/'
                          ? 'text-[#0284C7]'
                          : 'text-slate-700 hover:text-[#0284C7]'
                      }`}
                    >
                      {item.name}
                      {item.hasDropdown && (
                        <ChevronDown className={`w-4 h-4 transition-transform ${
                          openDropdown === getServiceSlug(item.name) ? 'rotate-180' : ''
                        }`} />
                      )}
                      {location.pathname.startsWith(item.path) && item.path !== '/' && (
                        <span className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#0284C7] rounded-full"></span>
                      )}
                    </Link>

                    {item.hasDropdown && (
                      <ServiceDropdown
                        service={getServiceSlug(item.name)}
                        isOpen={openDropdown === getServiceSlug(item.name)}
                        onClose={() => setOpenDropdown(null)}
                      />
                    )}
                  </div>
                ))}
              </nav>

              <Link
                to="/contact"
                data-testid="header-book-btn"
                className="group inline-flex items-center gap-2.5 bg-[#0F172A] hover:bg-[#1E293B] text-white font-heading font-bold uppercase tracking-wider text-[13px] px-5 lg:px-6 py-2.5 rounded-xl shadow-md shadow-slate-900/20 hover:shadow-lg hover:shadow-slate-900/30 hover:-translate-y-0.5 transition-all ring-1 ring-white/5 flex-shrink-0"
              >
                <CalendarCheck className="w-4 h-4 text-[#38BDF8] group-hover:text-white transition-colors" strokeWidth={2.4} />
                <span>Book Service</span>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-x-0 top-[128px] z-40 md:hidden bg-white shadow-xl border-b border-slate-200 overflow-hidden"
          >
            <nav className="flex flex-col p-4 max-h-[calc(100vh-150px)] overflow-y-auto">
              {navItems.map((item) => (
                <div key={item.name} className="border-b border-slate-100 last:border-0">
                  {item.hasDropdown ? (
                    <>
                      <button
                        onClick={() => setExpandedMobileService(
                          expandedMobileService === getServiceSlug(item.name)
                            ? null
                            : getServiceSlug(item.name)
                        )}
                        className="w-full flex items-center justify-between text-base font-heading font-bold text-slate-800 py-3 hover:text-[#0284C7]"
                      >
                        <span>{item.name}</span>
                        <ChevronRight className={`w-5 h-5 transition-transform ${
                          expandedMobileService === getServiceSlug(item.name) ? 'rotate-90' : ''
                        }`} />
                      </button>
                      <AnimatePresence>
                        {expandedMobileService === getServiceSlug(item.name) && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="ml-4 pb-3 space-y-1 overflow-hidden"
                          >
                            <Link
                              to={item.path}
                              className="block text-sm text-[#0284C7] font-semibold py-2"
                            >
                              All {item.name} Services →
                            </Link>
                            {serviceStructure[getServiceSlug(item.name)]?.subServices.map((sub) => (
                              <Link
                                key={sub.slug}
                                to={`/${getServiceSlug(item.name)}/${sub.slug}`}
                                className="block text-sm text-slate-600 hover:text-[#0284C7] py-1.5"
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  ) : (
                    <Link
                      to={item.path}
                      className="block text-base font-heading font-bold text-slate-800 py-3 hover:text-[#0284C7]"
                    >
                      {item.name}
                    </Link>
                  )}
                </div>
              ))}

              {/* Mobile CTA Section */}
              <div className="pt-4 mt-2 border-t border-slate-200 space-y-3">
                <Link
                  to="/contact"
                  data-testid="mobile-book-btn"
                  className="flex items-center justify-center gap-2.5 bg-[#0F172A] hover:bg-[#1E293B] text-white font-heading font-bold uppercase tracking-wider text-sm px-6 py-3.5 rounded-xl shadow-md shadow-slate-900/20 transition-all"
                >
                  <CalendarCheck className="w-5 h-5 text-[#38BDF8]" strokeWidth={2.4} />
                  <span>Book Service</span>
                </Link>
                <a
                  href={`tel:${PHONE}`}
                  data-testid="mobile-call-btn"
                  className="flex items-center justify-center gap-2 text-[#0F172A] text-lg font-bold py-2 rounded-xl border border-slate-200 hover:border-[#0284C7] hover:text-[#0284C7] transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  Call {PHONE}
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
